import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { cachedPlacesFetch, PLACES_TTL } from "./places-http-cache.server";
import { reserveGoogleCall } from "./google-provider-gateway.server";
import { readCatalogNearPoint, writeCatalogCandidates } from "./place-catalog.server";




const PLACES_BASE = "https://places.googleapis.com/v1";
const MAPS_BASE = "https://maps.googleapis.com";

// Server key — used only for server-to-server Places REST calls.
// Restrict in Google Cloud Console by IP or keep unrestricted but never expose to clients.
function getServerKey() {
  const key = process.env.GOOGLE_MAPS_SERVER_API_KEY;
  if (!key) throw new Error("GOOGLE_MAPS_SERVER_API_KEY is not configured");
  return key;
}

// Browser key — exposed to clients for the Maps JS SDK.
// MUST be restricted by HTTP referrer in Google Cloud Console.
function getBrowserKey() {
  const key = process.env.GOOGLE_MAPS_API_KEY;
  if (!key) throw new Error("GOOGLE_MAPS_API_KEY is not configured");
  return key;
}

export const getMapsBrowserKey = createServerFn({ method: "GET" }).handler(async () => {
  return { key: getBrowserKey() };
});

const searchInput = z.object({
  query: z.string().min(1).max(300),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  radiusMeters: z.number().min(500).max(80000).optional(),
});

const haversineMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const locationRectangle = (lat: number, lng: number, radiusMeters: number) => {
  const latDelta = radiusMeters / 111_320;
  const lngDelta = radiusMeters / (111_320 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
  return {
    low: { latitude: clamp(lat - latDelta, -90, 90), longitude: clamp(lng - lngDelta, -180, 180) },
    high: { latitude: clamp(lat + latDelta, -90, 90), longitude: clamp(lng + lngDelta, -180, 180) },
  };
};

async function placesSearchText(body: Record<string, unknown>) {
  const r = await cachedPlacesFetch(
    `${PLACES_BASE}/places:searchText`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": getServerKey(),
        "X-Goog-FieldMask": "places.id,places.location,places.photos,places.displayName",
      },
      body: JSON.stringify(body),
    },
    { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "map_marker", sku: "places_text_search" },
  );
  if (!r.ok) {
    console.error("Places searchText failed", r.status, await r.text().catch(() => ""));
    return null;
  }
  return (await r.json()) as {
    places?: Array<{
      id?: string;
      displayName?: { text?: string };
      location?: { latitude?: number; longitude?: number };
      photos?: Array<{ name?: string }>;
    }>;
  };
}

export const searchPlace = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => searchInput.parse(data))
  .handler(async ({ data }) => {
    try {
      const { checkPlaceLookupRateLimit } = await import("./quota.server");
      const rl = await checkPlaceLookupRateLimit();
      if (!rl.ok) {
        return { placeId: null, photoUrl: null, lat: null, lng: null };
      }
      const radius = data.radiusMeters ?? 25000;
      const center = { latitude: data.lat, longitude: data.lng };

      // First: hard restriction around the city/user centre — Places API will
      // NOT return results outside this area, so names matching another
      // continent get discarded instead.
      let j = await placesSearchText({
        textQuery: data.query,
        maxResultCount: 1,
        locationRestriction: { rectangle: locationRectangle(data.lat, data.lng, radius) },
      });

      // Fallback: if nothing matched within the strict radius, retry with bias
      // at a wider radius but still validate the result distance afterwards.
      if (!j?.places?.[0]) {
        j = await placesSearchText({
          textQuery: data.query,
          maxResultCount: 1,
          locationBias: { circle: { center, radius: Math.min(radius * 2, 50000) } },
        });
      }

      const p = j?.places?.[0];
      if (p?.location?.latitude == null || p.location.longitude == null) {
        return { placeId: null, photoUrl: null, lat: null, lng: null };
      }

      // Final sanity-check: drop results too far from the user's centre even if
      // the API returned them (guards the bias fallback).
      const dist = haversineMeters(
        { lat: data.lat, lng: data.lng },
        { lat: p.location.latitude, lng: p.location.longitude },
      );
      const maxAllowed = Math.max(radius, 30000);
      if (dist > maxAllowed) {
        console.warn(`searchPlace: rejected result ${dist.toFixed(0)}m away for "${data.query}"`);
        return { placeId: null, photoUrl: null, lat: null, lng: null };
      }

      const photoName = p.photos?.[0]?.name;
      let photoUrl: string | null = null;
      if (photoName) {
        try {
          const ph = await cachedPlacesFetch(
            `${PLACES_BASE}/${photoName}/media?skipHttpRedirect=true&maxWidthPx=800`,
            { method: "GET", headers: { "X-Goog-Api-Key": getServerKey() } },
            { kind: "photo", ttlMs: 0, origin: "map_marker", sku: "place_photo" },
          );
          if (ph.ok) {
            const pj = (await ph.json()) as { photoUri?: string };
            photoUrl = pj?.photoUri ?? null;
          }
        } catch (e) {
          console.error("Places photo media failed", e);
        }
      }
      return {
        placeId: p.id ?? null,
        photoUrl,
        lat: p.location.latitude,
        lng: p.location.longitude,
      };
    } catch (e) {
      console.error("searchPlace error", e);
      return { placeId: null, photoUrl: null, lat: null, lng: null };
    }
  });

// Resolve a public CDN photo URL for a known placeId (used by the manual stop
// flow). Returns a `lh3.googleusercontent.com` URL that does not require an
// API key or referrer — safe to use from any domain.
export const getPlacePhoto = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ placeId: z.string().min(1).max(200) }).parse(d))
  .handler(async ({ data }) => {
    try {
      const { checkPlaceLookupRateLimit } = await import("./quota.server");
      const rl = await checkPlaceLookupRateLimit();
      if (!rl.ok) return { photoUrl: null as string | null };

      const r = await cachedPlacesFetch(
        `${PLACES_BASE}/places/${encodeURIComponent(data.placeId)}`,
        { method: "GET", headers: { "X-Goog-Api-Key": getServerKey(), "X-Goog-FieldMask": "photos" } },
        { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_place_photo", sku: "place_details" },
      );
      if (!r.ok) {
        console.error("getPlacePhoto details failed", r.status, await r.text().catch(() => ""));
        return { photoUrl: null as string | null };
      }
      const j = (await r.json()) as { photos?: Array<{ name?: string }> };
      const photoName = j?.photos?.[0]?.name;
      if (!photoName) return { photoUrl: null as string | null };

      const ph = await cachedPlacesFetch(
        `${PLACES_BASE}/${photoName}/media?skipHttpRedirect=true&maxWidthPx=800`,
        { method: "GET", headers: { "X-Goog-Api-Key": getServerKey() } },
        { kind: "photo", ttlMs: 0, origin: "route_place_photo", sku: "place_photo" },
      );
      if (!ph.ok) return { photoUrl: null as string | null };
      const pj = (await ph.json()) as { photoUri?: string };
      return { photoUrl: pj?.photoUri ?? null };
    } catch (e) {
      console.error("getPlacePhoto error", e);
      return { photoUrl: null as string | null };
    }
  });

// Fetch contact info (phone, website, price level, types) for a known placeId.
// Used by the stop detail dialog to surface "Call", "Website" and
// Booking/Airbnb deep-links without spending AI credits.
export type PlaceContactInfo = {
  phone: string | null;
  website: string | null;
  googleMapsUri: string | null;
  priceLevel: number | null; // 1..4
  types: string[];
  primaryType: string | null;
};

export const getPlaceContactInfo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ placeId: z.string().min(1).max(200) }).parse(d))
  .handler(async ({ data }): Promise<PlaceContactInfo> => {
    const empty: PlaceContactInfo = {
      phone: null, website: null, googleMapsUri: null,
      priceLevel: null, types: [], primaryType: null,
    };
    const cacheKey = `contact|${data.placeId}`;
    let supabaseAdmin: any = null;
    try {
      ({ supabaseAdmin } = await import("@/integrations/supabase/client.server"));
      const { data: cached } = await supabaseAdmin
        .from("place_enrichment_cache")
        .select("payload,hits,expires_at")
        .eq("cache_key", cacheKey)
        .maybeSingle();
      const exp = (cached as any)?.expires_at as string | null | undefined;
      const isExpired = exp ? new Date(exp).getTime() < Date.now() : false;
      if (cached?.payload && !isExpired) {
        void supabaseAdmin
          .from("place_enrichment_cache")
          .update({ last_used_at: new Date().toISOString(), hits: (cached.hits ?? 0) + 1 })
          .eq("cache_key", cacheKey);
        return cached.payload as PlaceContactInfo;
      }

    } catch (e) {
      console.warn("place_enrichment_cache read failed", e);
    }

    try {
      const { checkPlaceLookupRateLimit } = await import("./quota.server");
      const rl = await checkPlaceLookupRateLimit();
      if (!rl.ok) return empty;

      const r = await cachedPlacesFetch(
        `${PLACES_BASE}/places/${encodeURIComponent(data.placeId)}`,
        {
          method: "GET",
          headers: {
            "X-Goog-Api-Key": getServerKey(),
            "X-Goog-FieldMask": "internationalPhoneNumber,nationalPhoneNumber,websiteUri,googleMapsUri,priceLevel,types,primaryType",
          },
        },
        { kind: "attractions", ttlMs: PLACES_TTL.attractions, origin: "route_place_details", sku: "place_details" },
      );
      if (!r.ok) {
        console.error("getPlaceContactInfo failed", r.status, await r.text().catch(() => ""));
        return empty;
      }
      const j = (await r.json()) as {
        internationalPhoneNumber?: string;
        nationalPhoneNumber?: string;
        websiteUri?: string;
        googleMapsUri?: string;
        priceLevel?: string;
        types?: string[];
        primaryType?: string;
      };
      const priceMap: Record<string, number> = {
        PRICE_LEVEL_FREE: 0,
        PRICE_LEVEL_INEXPENSIVE: 1,
        PRICE_LEVEL_MODERATE: 2,
        PRICE_LEVEL_EXPENSIVE: 3,
        PRICE_LEVEL_VERY_EXPENSIVE: 4,
      };
      const result: PlaceContactInfo = {
        phone: j.internationalPhoneNumber ?? j.nationalPhoneNumber ?? null,
        website: j.websiteUri ?? null,
        googleMapsUri: j.googleMapsUri ?? null,
        priceLevel: j.priceLevel != null ? (priceMap[j.priceLevel] ?? null) : null,
        types: j.types ?? [],
        primaryType: j.primaryType ?? null,
      };
      if (supabaseAdmin) {
        // 30 days — phones/websites/price levels change more often.
        const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
        void supabaseAdmin.from("place_enrichment_cache").upsert({
          cache_key: cacheKey,
          kind: "contact",
          place_id: data.placeId,
          payload: result,
          expires_at: expiresAt,
        }, { onConflict: "cache_key" });
      }
      return result;

    } catch (e) {
      console.error("getPlaceContactInfo error", e);
      return empty;
    }
  });

const staticRouteMapInput = z.object({
  stops: z.array(z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  })).min(1).max(25),
  venues: z.array(z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    kind: z.enum(["lunch", "dinner", "hotel"]),
  })).max(20).optional(),
  encodedPolyline: z.string().min(1).max(12000).nullable().optional(),
  width: z.number().int().min(320).max(1200).optional(),
  height: z.number().int().min(180).max(800).optional(),
});

function markerLabel(index: number) {
  if (index < 9) return String(index + 1);
  return String.fromCharCode(65 + Math.min(index - 9, 25));
}

export const getStaticRouteMapDataUrl = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => staticRouteMapInput.parse(data))
  .handler(async ({ data }) => {
    const { checkHourlyRateLimit } = await import("./quota.server");
    const rl = await checkHourlyRateLimit("static_map", 120);
    if (!rl.ok) return { dataUrl: null as string | null };
    const params = new URLSearchParams({
      size: `${data.width ?? 900}x${data.height ?? 420}`,
      scale: "2",
      format: "jpg",
      maptype: "roadmap",
      key: getServerKey(),
    });

    const stops = data.stops.slice(0, 25);
    for (const [idx, stop] of stops.entries()) {
      params.append("markers", `color:0x0ea5b7|label:${markerLabel(idx)}|${stop.lat},${stop.lng}`);
    }

    for (const venue of data.venues ?? []) {
      const color = venue.kind === "hotel" ? "0x7c3aed" : "0xf97316";
      const label = venue.kind === "hotel" ? "H" : "R";
      params.append("markers", `color:${color}|label:${label}|${venue.lat},${venue.lng}`);
    }

    if (data.encodedPolyline) {
      params.append("path", `color:0x0ea5b7ff|weight:5|enc:${data.encodedPolyline}`);
    } else if (stops.length > 1) {
      params.append("path", `color:0x0ea5b7ff|weight:5|${stops.map((s) => `${s.lat},${s.lng}`).join("|")}`);
    }

    const staticMapUrl = `${MAPS_BASE}/maps/api/staticmap?${params.toString()}`;
    const reservation = await reserveGoogleCall({
      url: staticMapUrl,
      method: "GET",
      sku: "static_map",
      origin: "route_static_map",
    });
    if (!reservation.allowed) return { dataUrl: null as string | null };
    const response = await fetch(staticMapUrl);
    reservation.settle({ status: response.status, ok: response.ok });
    if (!response.ok) {
      console.error("Static map failed", response.status, await response.text().catch(() => ""));
      return { dataUrl: null as string | null };
    }
    const contentType = response.headers.get("content-type") || "image/jpeg";
    const buffer = Buffer.from(await response.arrayBuffer());
    return { dataUrl: `data:${contentType};base64,${buffer.toString("base64")}` };
  });


// ---- Nearby restaurants (Places API New: searchNearby) ----
// Used by the meal-break "ver mais restaurantes" modal so users can pick
// alternatives near the previous stop. One Places call per modal open.

export type NearbyRestaurant = {
  placeId: string;
  name: string;
  address: string | null;
  rating: number | null;
  userRatingCount: number | null;
  priceLevel: number | null; // 1..4
  googleMapsUri: string | null;
  lat: number;
  lng: number;
  photoUrl: string | null;
};

const nearbyRestoInput = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  radiusMeters: z.number().min(200).max(5000).optional(),
  maxBudget: z.number().min(1).max(500).optional(),
  generationId: z.string().min(3).max(100).optional(),
});

const PRICE_LEVEL_MAP: Record<string, number> = {
  PRICE_LEVEL_INEXPENSIVE: 1,
  PRICE_LEVEL_MODERATE: 2,
  PRICE_LEVEL_EXPENSIVE: 3,
  PRICE_LEVEL_VERY_EXPENSIVE: 4,
};

export const findNearbyRestaurants = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => nearbyRestoInput.parse(d))
  .handler(async ({ data }): Promise<{ results: NearbyRestaurant[] }> => {
    try {
      const radiusMeters = data.radiusMeters ?? 1200;
      const catalog = await readCatalogNearPoint({
        lat: data.lat,
        lng: data.lng,
        radiusMeters,
        themeId: "meal:restaurant",
        limit: 12,
      });
      if (catalog.length >= 4) {
        return {
          results: catalog.slice(0, 8).map((place) => ({
            placeId: place.placeId,
            name: place.name,
            address: null,
            rating: place.rating,
            userRatingCount: null,
            priceLevel: null,
            googleMapsUri: `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(place.placeId)}`,
            lat: place.lat,
            lng: place.lng,
            photoUrl: null,
          })),
        };
      }
      const { checkPlaceLookupRateLimit } = await import("./quota.server");
      const rl = await checkPlaceLookupRateLimit();
      if (!rl.ok) return { results: [] };

      const r = await cachedPlacesFetch(`${PLACES_BASE}/places:searchNearby`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": getServerKey(),
          "X-Goog-FieldMask":
            "places.id,places.displayName,places.formattedAddress,places.location,places.rating,places.userRatingCount,places.priceLevel,places.googleMapsUri,places.photos",
        },
        body: JSON.stringify({
          includedTypes: ["restaurant"],
          maxResultCount: 12,
          rankPreference: "POPULARITY",
          locationRestriction: {
            circle: {
              center: { latitude: data.lat, longitude: data.lng },
              radius: radiusMeters,
            },
          },
        }),
      }, { kind: "dining", ttlMs: PLACES_TTL.dining, origin: "route_nearby_dining", sku: "places_nearby_search", generationId: data.generationId });
      if (!r.ok) {
        console.error("Places searchNearby failed", r.status, await r.text().catch(() => ""));
        return { results: [] };
      }
      const j = (await r.json()) as {
        places?: Array<{
          id?: string;
          displayName?: { text?: string };
          formattedAddress?: string;
          rating?: number;
          userRatingCount?: number;
          priceLevel?: string;
          googleMapsUri?: string;
          location?: { latitude?: number; longitude?: number };
          photos?: Array<{ name?: string }>;
        }>;
      };

      const budgetCap = data.maxBudget;
      // Rough heuristic: priceLevel 1 ≈ ≤15, 2 ≈ 15-30, 3 ≈ 30-60, 4 ≈ 60+.
      const PRICE_TO_AVG: Record<number, number> = { 1: 12, 2: 22, 3: 45, 4: 80 };

      const out: NearbyRestaurant[] = [];
      for (const p of j.places ?? []) {
        if (!p.id || p.location?.latitude == null || p.location.longitude == null) continue;
        const pl = p.priceLevel ? (PRICE_LEVEL_MAP[p.priceLevel] ?? null) : null;
        if (budgetCap && pl && PRICE_TO_AVG[pl] > budgetCap) continue;
        out.push({
          placeId: p.id,
          name: p.displayName?.text ?? "Restaurante",
          address: p.formattedAddress ?? null,
          rating: typeof p.rating === "number" ? p.rating : null,
          userRatingCount: typeof p.userRatingCount === "number" ? p.userRatingCount : null,
          priceLevel: pl,
          googleMapsUri: p.googleMapsUri ?? null,
          lat: p.location.latitude,
          lng: p.location.longitude,
          photoUrl: null,
        });
      }
      // Sort by rating desc, then review count desc
      out.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || (b.userRatingCount ?? 0) - (a.userRatingCount ?? 0));
      void writeCatalogCandidates(out.map((place) => ({
        placeId: place.placeId,
        themeId: "meal:restaurant",
        name: place.name,
        lat: place.lat,
        lng: place.lng,
        rating: place.rating,
        types: ["restaurant"],
        photoName: null,
        addressComponents: null,
        openingPeriods: null,
      })));
      return { results: out.slice(0, 8) };
    } catch (e) {
      console.error("findNearbyRestaurants error", e);
      return { results: [] };
    }
  });

